import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { reglaPropinaService, ReglaPropina, ReglaPropinaCreate, ReglaPropinaUpdate } from '../../services/reglaPropinaService';
import { sucursalService } from '../../services/sucursalService';
import ReglaPropinaForm from '../../components/ReglasPropinas/ReglaPropinaForm';

const ReglasPropinasList = () => {
  const [showForm, setShowForm] = useState(false);
  const [reglaEditando, setReglaEditando] = useState<ReglaPropina | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    'reglas-propinas',
    () => reglaPropinaService.getAll()
  );

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const deleteMutation = useMutation(
    (id: number) => reglaPropinaService.delete(id),
    {
      onSuccess: () => {
        toast.success('Regla de propina eliminada correctamente');
        queryClient.invalidateQueries(['reglas-propinas']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al eliminar regla de propina');
      }
    }
  );

  const handleEdit = (regla: ReglaPropina) => {
    setReglaEditando(regla);
    setShowForm(true);
  };

  const handleCreate = () => {
    setReglaEditando(null);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setReglaEditando(null);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de eliminar esta regla de propina?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return <div>Cargando reglas de propinas...</div>;
  if (error) return <div>Error al cargar reglas de propinas</div>;

  const reglas = data?.data.reglas || [];
  const sucursales = sucursalesData?.data.sucursales || [];

  const getSucursalName = (sucursalId?: number) => {
    if (!sucursalId) return 'Global';
    const sucursal = sucursales.find(s => s.id === sucursalId);
    return sucursal?.name || 'N/A';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Reglas de Propinas</h1>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          + Nueva Regla
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sucursal
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rango Mín/Máx
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reglas.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No hay reglas de propinas registradas
                </td>
              </tr>
            ) : (
              reglas.map((regla) => (
                <tr key={regla.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {regla.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {regla.tipo === 'porcentaje' ? 'Porcentaje' : 'Fijo'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {regla.tipo === 'porcentaje' ? `${regla.valor}%` : `$${regla.valor.toFixed(2)}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getSucursalName(regla.sucursalId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {regla.minimoOrden || regla.maximoOrden
                      ? `${regla.minimoOrden ? `$${regla.minimoOrden}` : '$0'} - ${regla.maximoOrden ? `$${regla.maximoOrden}` : '∞'}`
                      : 'Sin límite'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        regla.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {regla.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(regla)}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(regla.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ReglaPropinaForm
          regla={reglaEditando}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default ReglasPropinasList;

